import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"

const API_URL = process.env.REACT_APP_API_URL

export const fetchDocuments = createAsyncThunk("documents/fetchDocuments", async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.get(`${API_URL}/documents`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return response.data.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const uploadDocument = createAsyncThunk("documents/uploadDocument", async (formData, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.post(`${API_URL}/documents/upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const updateDocument = createAsyncThunk(
  "documents/updateDocument",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const response = await axios.put(`${API_URL}/documents/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      })
      return response.data.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

export const deleteDocument = createAsyncThunk("documents/deleteDocument", async (id, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    await axios.delete(`${API_URL}/documents/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return id
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const viewDocument = createAsyncThunk("documents/viewDocument", async (id, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.get(`${API_URL}/documents/${id}/view`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    })
    return { id, blob: response.data }
  } catch (error) {
    try {
      if (error?.response?.data instanceof Blob) {
        const text = await error.response.data.text()
        const parsed = JSON.parse(text)
        return rejectWithValue(parsed?.message || error?.response?.statusText || "Failed to view document")
      }
    } catch (_) {}
    return rejectWithValue(error.response?.data?.message || error.message || "Failed to view document")
  }
})

export const downloadDocument = createAsyncThunk("documents/downloadDocument", async (id, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.get(`${API_URL}/documents/${id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    })
    return { id, blob: response.data }
  } catch (error) {
    try {
      if (error?.response?.data instanceof Blob) {
        const text = await error.response.data.text()
        const parsed = JSON.parse(text)
        return rejectWithValue(parsed?.message || error?.response?.statusText || "Failed to download document")
      }
    } catch (_) {}
    return rejectWithValue(error.response?.data?.message || error.message || "Failed to download document")
  }
})

const documentSlice = createSlice({
  name: "documents",
  initialState: {
    documents: [],
    loading: false,
    error: null,
    viewedDocuments: {},
  },
  reducers: {
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocuments.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.loading = false
        state.documents = action.payload
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(uploadDocument.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.loading = false
        // Removed push; fetchDocuments called in component
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(updateDocument.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateDocument.fulfilled, (state, action) => {
        state.loading = false
        const index = state.documents.findIndex((d) => d.doc_id === action.payload.doc_id)
        if (index !== -1) {
          state.documents[index] = action.payload
        }
      })
      .addCase(updateDocument.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.documents = state.documents.filter((d) => d.doc_id !== action.payload)
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(viewDocument.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(viewDocument.fulfilled, (state, action) => {
        state.loading = false
        state.viewedDocuments[action.payload.id] = action.payload.blob
      })
      .addCase(viewDocument.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(downloadDocument.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(downloadDocument.fulfilled, (state, action) => {
        state.loading = false
        // Handle download logic here if needed
      })
      .addCase(downloadDocument.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearError } = documentSlice.actions
export default documentSlice.reducer
